import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, ArrowUpDown, DollarSign, Clock, CheckCircle, AlertCircle,
  Home, Menu, X, Globe, User, LogOut, Store, BarChart3, Loader2,
  Banknote, Receipt, Send, RefreshCw, MessageSquare, XCircle, SkipForward,
  TrendingUp, Play, FileText, Download, RotateCcw, ShieldAlert, Webhook,
  AlertTriangle, Eye, Zap, CreditCard, Settings
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

// Import utilities from admin components
import { 
  API_URL as ADMIN_API_URL, 
  fadeInUp, 
  getStatusColor as getStatusColorUtil,
  formatDateTime,
  formatCurrency
} from '../../components/admin/adminUtils';

// Import modular admin tab components (with i18n support)
import { 
  MerchantsTab,
  TransactionsTab,
  PayoutsTab,
  ReceiptsTab,
  RefundsTab,
  DisputesTab,
  WebhooksTab,
  PilotOpsTab,
  PaymentsTab,
} from '../../components/admin/tabs';

import { API_URL } from '../../utils/apiConfig';
import FXRateManager from '../../components/FXRateManager';

// fadeInUp is imported from adminUtils

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('merchants');
  const [stats, setStats] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [merchantBalances, setMerchantBalances] = useState([]);
  const [receipts, setReceipts] = useState([]);
  // Phase-1: SMS disabled - state kept for Phase-2 expansion
  // const [smsLogs, setSmsLogs] = useState([]);
  // const [smsStats, setSmsStats] = useState(null);
  // const [smsConfig, setSmsConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [payoutActionDialog, setPayoutActionDialog] = useState(false);
  const [refunds, setRefunds] = useState([]);
  const [refundStats, setRefundStats] = useState(null);
  const [disputes, setDisputes] = useState([]);
  const [disputeStats, setDisputeStats] = useState(null);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [webhookStats, setWebhookStats] = useState(null);
  const [pilotMode, setPilotMode] = useState(null);
  const [refundDialog, setRefundDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [createPayoutOpen, setCreatePayoutOpen] = useState(false);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [fxManagerOpen, setFxManagerOpen] = useState(false);

  const { language, toggleLanguage, t } = useLanguage();
  const { user, token, logout } = useAuth();

  useEffect(() => {
    if (token) {
      fetchStats();
      fetchMerchants();
      fetchTransactions();
      fetchPayouts();
      fetchMerchantBalances();
      fetchReceipts();
      // Phase-1: SMS disabled - calls removed
      // fetchSmsLogs();
      // fetchSmsConfig();
      fetchRefunds();
      fetchDisputes();
      fetchWebhookEvents();
      fetchPilotMode();
    }
  }, [token]);

  const fetchPilotMode = async () => {
    try {
      const response = await fetch(`${API_URL}/api/health`);
      if (response.ok) {
        const data = await response.json();
        setPilotMode({
          enabled: data.features?.pilot_mode || false,
          provider: data.features?.payment_provider || 'unknown',
          isSimulated: data.features?.is_simulated || false,
        });
      }
    } catch (error) {
      console.error('Failed to fetch pilot mode:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchMerchants = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/merchants`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setMerchants(data.merchants || []);
      }
    } catch (error) {
      console.error('Failed to fetch merchants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchantBalances = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payouts/balances`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setMerchantBalances(data.balances || []);
      }
    } catch (error) {
      console.error('Failed to fetch merchant balances:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/transactions`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const fetchPayouts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payouts/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPayouts(data.payouts || []);
      }
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
    }
  };

  const fetchReceipts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/receipts`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setReceipts(data.receipts || []);
      }
    } catch (error) {
      console.error('Failed to fetch receipts:', error);
    }
  };

  /* Phase-1: SMS functions disabled - kept for Phase-2 expansion
  const fetchSmsLogs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sms/logs`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSmsLogs(data.logs || []);
        setSmsStats(data.stats || null);
      }
    } catch (error) {
      console.error('Failed to fetch SMS logs:', error);
    }
  };

  const fetchSmsConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sms/config`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSmsConfig(data);
      }
    } catch (error) {
      console.error('Failed to fetch SMS config:', error);
    }
  };

  const handleTestSms = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sms/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.test_result.status === 'sent') {
        toast.success('Test SMS sent successfully');
      } else {
        toast.info(`SMS ${data.test_result.status}: ${data.test_result.reason}`);
      }
      fetchSmsLogs();
    } catch (error) {
      toast.error('Failed to send test SMS');
    }
  };
  // End Phase-1 SMS disabled */

  const fetchPayoutSummary = async (merchantId) => {
    try {
      const response = await fetch(`${API_URL}/api/payouts/merchant/${merchantId}/summary`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPayoutSummary(data);
      }
    } catch (error) {
      console.error('Failed to fetch payout summary:', error);
    }
  };

  const approveMerchant = async (merchantId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/merchants/${merchantId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success('Merchant approved successfully');
        fetchMerchants();
        fetchStats();
      } else {
        toast.error('Failed to approve merchant');
      }
    } catch (error) {
      toast.error('Failed to approve merchant');
    }
  };

  // Payout workflow actions
  const handlePayoutAction = async (payoutId, action, notes = '') => {
    try {
      const response = await fetch(`${API_URL}/api/payouts/${payoutId}/${action}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes })
      });
      
      if (response.ok) {
        toast.success(`Payout ${action} successful`);
        fetchPayouts();
        fetchMerchantBalances();
        setPayoutActionDialog(false);
        setSelectedPayout(null);
      } else {
        const data = await response.json();
        toast.error(data.detail || `Failed to ${action} payout`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} payout`);
    }
  };

  const handleExportPayout = async (payoutId) => {
    try {
      const response = await fetch(`${API_URL}/api/payouts/${payoutId}/export`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        // Create and download CSV
        const blob = new Blob([data.csv_data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('CSV exported successfully');
      }
    } catch (error) {
      toast.error('Failed to export payout');
    }
  };

  // Refunds & Disputes fetch functions
  const fetchRefunds = async () => {
    try {
      const response = await fetch(`${API_URL}/api/refunds/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRefunds(data.refunds || []);
        setRefundStats(data.by_status);
      }
    } catch (error) {
      console.error('Failed to fetch refunds:', error);
    }
  };

  const fetchDisputes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/disputes/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDisputes(data.disputes || []);
        setDisputeStats(data.by_status);
      }
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
    }
  };

  const fetchWebhookEvents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/webhooks/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setWebhookEvents(data.events || []);
        setWebhookStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch webhook events:', error);
    }
  };

  const handleCreateRefund = async () => {
    if (!selectedTransaction || !refundReason) {
      toast.error('Please provide a reason for the refund');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/refunds/`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transaction_id: selectedTransaction.id,
          amount: refundAmount ? parseFloat(refundAmount) : null,
          reason: refundReason
        })
      });

      if (response.ok) {
        toast.success('Refund created successfully');
        setRefundDialog(false);
        setSelectedTransaction(null);
        setRefundAmount('');
        setRefundReason('');
        fetchRefunds();
        fetchTransactions();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to create refund');
      }
    } catch (error) {
      toast.error('Failed to create refund');
    }
  };

  const handleProcessRefund = async (refundId) => {
    try {
      const response = await fetch(`${API_URL}/api/refunds/${refundId}/process`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Refund processed successfully');
        fetchRefunds();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to process refund');
      }
    } catch (error) {
      toast.error('Failed to process refund');
    }
  };

  const handleReprocessWebhook = async (eventId) => {
    try {
      const response = await fetch(`${API_URL}/api/webhooks/${eventId}/reprocess`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Webhook reprocessed');
        fetchWebhookEvents();
      } else {
        toast.error('Failed to reprocess webhook');
      }
    } catch (error) {
      toast.error('Failed to reprocess webhook');
    }
  };

  const handleOpenPayoutDialog = async (merchant) => {
    setSelectedMerchant(merchant);
    await fetchPayoutSummary(merchant.id);
    setCreatePayoutOpen(true);
  };

  const handleCreatePayout = async () => {
    if (!payoutSummary || payoutSummary.unpaid_transaction_ids.length === 0) {
      toast.error('No unpaid transactions to include in payout');
      return;
    }

    setPayoutLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/payouts/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          merchant_id: selectedMerchant.id,
          transaction_ids: payoutSummary.unpaid_transaction_ids,
          notes: `Payout batch for ${selectedMerchant.business_name}`,
        }),
      });

      if (response.ok) {
        toast.success('Payout created successfully');
        setCreatePayoutOpen(false);
        fetchPayouts();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create payout');
      }
    } catch (error) {
      toast.error('Failed to create payout');
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleUpdatePayoutStatus = async (payoutId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/payouts/${payoutId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        toast.success(`Payout ${newStatus}`);
        fetchPayouts();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update payout');
      }
    } catch (error) {
      toast.error('Failed to update payout');
    }
  };

  const handleResendReceipt = async (receiptId) => {
    try {
      const response = await fetch(`${API_URL}/api/receipts/${receiptId}/resend`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Receipt resent successfully');
      } else {
        toast.error('Failed to resend receipt');
      }
    } catch (error) {
      toast.error('Failed to resend receipt');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'active':
        return <CheckCircle className="w-4 h-4 text-emerald-600" />;
      case 'pending':
      case 'pending_approval':
      case 'approved':
        return <Clock className="w-4 h-4 text-amber-600" />;
      case 'processing':
        return <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />;
      case 'draft':
        return <FileText className="w-4 h-4 text-slate-500" />;
      case 'failed':
      case 'inactive':
      case 'suspended':
      case 'cancelled':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'active':
        return 'bg-emerald-50 text-emerald-700';
      case 'pending':
      case 'pending_approval':
        return 'bg-amber-50 text-amber-700';
      case 'approved':
        return 'bg-blue-50 text-blue-700';
      case 'processing':
        return 'bg-indigo-50 text-indigo-700';
      case 'draft':
        return 'bg-slate-100 text-slate-600';
      case 'failed':
      case 'inactive':
      case 'suspended':
      case 'cancelled':
        return 'bg-red-50 text-red-700';
      default:
        return 'bg-slate-50 text-slate-700';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-50 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center">
              <span className="text-white font-bold text-lg">P</span>
            </div>
            <span className="font-bold text-xl text-slate-900">PeCash</span>
          </Link>

          {/* Pilot Mode Badge */}
          {pilotMode?.enabled && (
            <div className="mb-6 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="pilot-mode-badge">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-600" />
                <span className="text-xs font-semibold text-amber-800">SIMULATED (PILOT MODE)</span>
              </div>
              <p className="text-xs text-amber-600 mt-0.5">Provider: {pilotMode.provider}</p>
            </div>
          )}

          <nav className="space-y-2">
            <Link to="/app/admin" className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-100 text-slate-900 font-medium" data-testid="sidebar-dashboard">
              <BarChart3 className="w-5 h-5" />
              {t('dashboard.admin.title')}
            </Link>
            <button onClick={() => setActiveTab('merchants')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === 'merchants' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="sidebar-merchants">
              <Store className="w-5 h-5" />
              Merchants
            </button>
            <button onClick={() => setActiveTab('transactions')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === 'transactions' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="sidebar-transactions">
              <ArrowUpDown className="w-5 h-5" />
              Transactions
            </button>
            <button onClick={() => setActiveTab('payouts')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === 'payouts' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="sidebar-payouts">
              <Banknote className="w-5 h-5" />
              Payouts
              {stats?.pending_payouts > 0 && (
                <span className="ml-auto bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">{stats.pending_payouts}</span>
              )}
            </button>
            <button onClick={() => setActiveTab('receipts')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === 'receipts' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="sidebar-receipts">
              <Receipt className="w-5 h-5" />
              Receipts
            </button>
            {/* SMS Tab - Hidden for Phase-1 (In-app notifications only)
            <button onClick={() => setActiveTab('sms')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === 'sms' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="sidebar-sms">
              <MessageSquare className="w-5 h-5" />
              SMS Logs
              {smsConfig && !smsConfig.sms_enabled && (
                <span className="ml-auto bg-slate-100 text-slate-500 text-xs px-2 py-0.5 rounded-full">Off</span>
              )}
            </button>
            */}
            <button onClick={() => setActiveTab('refunds')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === 'refunds' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="sidebar-refunds">
              <RotateCcw className="w-5 h-5" />
              Refunds
              {refundStats?.requested > 0 && (
                <span className="ml-auto bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">{refundStats.requested}</span>
              )}
            </button>
            <button onClick={() => setActiveTab('disputes')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === 'disputes' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="sidebar-disputes">
              <ShieldAlert className="w-5 h-5" />
              Disputes
              {disputeStats?.open > 0 && (
                <span className="ml-auto bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">{disputeStats.open}</span>
              )}
            </button>
            <button onClick={() => setActiveTab('webhooks')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === 'webhooks' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="sidebar-webhooks">
              <Webhook className="w-5 h-5" />
              Webhooks
              {webhookStats?.last_24h?.failed > 0 && (
                <span className="ml-auto bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">{webhookStats.last_24h.failed}</span>
              )}
            </button>
            {/* Pilot Payments Tab - visible in pilot mode */}
            {pilotMode?.enabled && (
              <button onClick={() => setActiveTab('payments')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === 'payments' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="sidebar-payments">
                <CreditCard className="w-5 h-5" />
                Payments
              </button>
            )}
            {/* Pilot Ops Tab - only visible in pilot mode */}
            {pilotMode?.enabled && (
              <button onClick={() => setActiveTab('pilot-ops')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === 'pilot-ops' ? 'bg-amber-50 text-amber-700' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="sidebar-pilot-ops">
                <Zap className="w-5 h-5" />
                Pilot Ops
                <span className="ml-auto bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">🧪</span>
              </button>
            )}
            <button 
              onClick={() => setFxManagerOpen(true)} 
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
              data-testid="sidebar-fx-rates"
            >
              <DollarSign className="w-5 h-5" />
              FX Rates
            </button>
            <Link to="/app/admin/analytics" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors" data-testid="sidebar-analytics">
              <TrendingUp className="w-5 h-5" />
              Analytics
            </Link>
            <Link to="/" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors" data-testid="sidebar-home">
              <Home className="w-5 h-5" />
              {t('nav.home')}
            </Link>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
              <User className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900 text-sm">{user?.name || 'Admin'}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={logout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-slate-200 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="mobile-sidebar-toggle">
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <h1 className="text-xl font-bold text-slate-900">{t('dashboard.admin.title')}</h1>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={toggleLanguage} data-testid="dashboard-language-toggle" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
                <Globe className="w-4 h-4" />
                <span>{language.toUpperCase()}</span>
              </button>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="p-6">
          <motion.div initial="initial" animate="animate" variants={{ animate: { transition: { staggerChildren: 0.1 } } }}>
            {/* Stats Grid */}
            <motion.div variants={fadeInUp} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="total-merchants-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-slate-500">{t('common.colmado')}s</span>
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_merchants || 0}</p>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="total-transactions-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-slate-500">{t('dashboard.admin.totalTransactions')}</span>
                  <ArrowUpDown className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_transactions?.toLocaleString() || 0}</p>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="total-volume-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-slate-500">{t('dashboard.admin.totalVolume')}</span>
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-2xl font-bold text-slate-900">${stats?.total_volume?.toLocaleString() || 0}</p>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="pending-payouts-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-slate-500">{t('dashboard.admin.pendingPayouts')}</span>
                  <Banknote className="w-5 h-5 text-amber-600" />
                </div>
                <p className="text-2xl font-bold text-slate-900">{stats?.pending_payouts || 0}</p>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="receipts-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-slate-500">{t('dashboard.admin.receipts')}</span>
                  <Receipt className="w-5 h-5 text-slate-600" />
                </div>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_receipts || 0}</p>
              </div>
            </motion.div>

            {/* Merchants Table */}
            {activeTab === 'merchants' && (
              <MerchantsTab
                merchants={merchants}
                loading={loading}
                onApprove={approveMerchant}
                onOpenPayoutDialog={handleOpenPayoutDialog}
                getStatusIcon={getStatusIcon}
                getStatusColor={getStatusColor}
              />
            )}

            {/* Transactions Table */}
            {activeTab === 'transactions' && (
              <TransactionsTab
                transactions={transactions}
                onRefundClick={(tx) => {
                  setSelectedTransaction(tx);
                  setRefundAmount(tx.amount?.toString() || '');
                  setRefundDialog(true);
                }}
                getStatusIcon={getStatusIcon}
                getStatusColor={getStatusColor}
                formatDate={formatDate}
              />
            )}

            {/* Payouts Table */}
            {activeTab === 'payouts' && (
              <PayoutsTab
                payouts={payouts}
                merchantBalances={merchantBalances}
                stats={stats}
                onPayoutAction={handlePayoutAction}
                onExportPayout={handleExportPayout}
                onOpenPayoutDialog={handleOpenPayoutDialog}
                getStatusIcon={getStatusIcon}
                getStatusColor={getStatusColor}
                formatDate={formatDate}
              />
            )}

            {/* Receipts Table */}
            {activeTab === 'receipts' && (
              <ReceiptsTab
                receipts={receipts}
                onResendReceipt={handleResendReceipt}
                getStatusIcon={getStatusIcon}
                getStatusColor={getStatusColor}
                formatDate={formatDate}
              />
            )}

            {/* SMS Logs Tab - HIDDEN FOR PHASE-1 (In-app notifications only)
               TODO: Phase-2 - Uncomment when SMS integration is ready
            */}

            {/* Refunds Tab */}
            {activeTab === 'refunds' && (
              <RefundsTab
                refunds={refunds}
                refundStats={refundStats}
                onProcessRefund={handleProcessRefund}
                onRefresh={fetchRefunds}
                getStatusIcon={getStatusIcon}
                getStatusColor={getStatusColor}
                formatDate={formatDate}
              />
            )}

            {/* Disputes Tab */}
            {activeTab === 'disputes' && (
              <DisputesTab
                disputes={disputes}
                disputeStats={disputeStats}
                onRefresh={fetchDisputes}
                formatDate={formatDate}
              />
            )}

            {/* Webhooks Tab */}
            {activeTab === 'webhooks' && (
              <WebhooksTab
                webhookEvents={webhookEvents}
                webhookStats={webhookStats}
                onReprocess={handleReprocessWebhook}
                onRefresh={fetchWebhookEvents}
                formatDate={formatDate}
                pilotMode={pilotMode}
              />
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && pilotMode?.enabled && (
              <PaymentsTab
                token={token}
              />
            )}

            {/* Pilot Ops Tab */}
            {activeTab === 'pilot-ops' && pilotMode?.enabled && (
              <PilotOpsTab
                token={token}
                t={t}
              />
            )}
          </motion.div>
        </main>
      </div>

      {/* Create Payout Dialog */}
      <Dialog open={createPayoutOpen} onOpenChange={setCreatePayoutOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-emerald-600" />
              Create Payout
            </DialogTitle>
            <DialogDescription>
              {t('payouts.createPayoutFor')} {selectedMerchant?.business_name}
            </DialogDescription>
          </DialogHeader>
          
          {payoutSummary ? (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('payouts.walletBalance')}</span>
                  <span className="font-semibold">${payoutSummary.wallet_balance?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('payouts.totalReceived')}</span>
                  <span className="font-semibold">${payoutSummary.total_received?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('payouts.alreadyPaidOut')}</span>
                  <span className="font-semibold text-emerald-600">${payoutSummary.total_paid_out?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t pt-3">
                  <span className="text-slate-700 font-medium">{t('payouts.unpaidAmount')}</span>
                  <span className="font-bold text-lg">${payoutSummary.unpaid_amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{language === 'es' ? 'Transacciones a incluir' : 'Transactions to include'}</span>
                  <span>{payoutSummary.unpaid_transaction_count}</span>
                </div>
              </div>
              
              <Button
                onClick={handleCreatePayout}
                disabled={payoutLoading || payoutSummary.unpaid_transaction_count === 0}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {payoutLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Banknote className="w-4 h-4 mr-2" />
                    {t('common.createPayout')} ${payoutSummary.unpaid_amount?.toFixed(2)}
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* FX Rate Manager Modal */}
      <FXRateManager 
        token={token}
        isOpen={fxManagerOpen}
        onClose={() => setFxManagerOpen(false)}
      />
    </div>
  );
}
